import { useEffect, useState } from "react"
import API from "../api"

/**
 * Fetches rows + statistics for a single OT signal from actual log data.
 *
 * Handles both document structures:
 *   - composite: normalized.points[].full_address
 *   - single:    normalized.full_address
 *
 * The returned rows already have `value` set to the signal-specific point value
 * (not the raw top-level document value).
 * Statistics are computed over ALL matching records in the period, not just the
 * rows returned (which are capped at 3000 for chart performance).
 *
 * @param {Object|null} payload
 *   - timeFilter: [isoFrom, isoTo]
 *   - full_address: string
 *   - filter?: FilterModel[]   same format as ot-log filter
 *   - mode?: "r" | "w" | null
 * @returns {{ rows: Array, stats: Object|null, loading: boolean }}
 */
const useOtSignalData = (payload) => {
  const [rows, setRows] = useState([])
  const [stats, setStats] = useState(null)
  const [configRange, setConfigRange] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!payload?.full_address || !payload?.timeFilter) {
      setRows([])
      setStats(null)
      setConfigRange(null)
      return
    }

    const controller = new AbortController()
    setLoading(true)
    setRows([])
    setStats(null)
    setConfigRange(null)

    API.getOtSignalData(payload, controller.signal)
      .then((res) => {
        const rawRows = res.data?.rows ?? []
        setRows(rawRows.map((r, idx) => ({ ...r, id: `sig-${idx}` })))
        setStats(res.data?.stats ?? null)
        setConfigRange(res.data?.config_range ?? null)
      })
      .catch((err) => {
        if (err.name !== "CanceledError" && err.name !== "AbortError") {
          console.error("[useOtSignalData] Failed to fetch OT signal data:", err)
        }
      })
      .finally(() => setLoading(false))

    return () => controller.abort()
  }, [JSON.stringify(payload)]) // eslint-disable-line react-hooks/exhaustive-deps

  return { rows, stats, configRange, loading }
}

export default useOtSignalData
